import type { Entry } from "@/shared/types/entries";
import { emptyDaySums, type DaySums } from "@/v2/pages/Calendar";

/**
 * Build a Map<dayKey, DaySums> from the two entry arrays exposed by DataProvider.
 *
 * Day key format is `YYYY-M-D` (matching the format the v2 Calendar reads).
 *
 * Billable bucketing rules:
 *   - Entry has no matter (matter_id is empty/null) → billable.
 *     A missing matter means there's no matters_tm row to read billable from;
 *     we default optimistically to billable.
 *   - Entry has a matter and matters_tm.billable === true → billable.
 *   - Entry has a matter and matters_tm.billable === false or null → non-billable.
 */
export function buildDailySums(
	entries: Entry[],
	exportedEntries: Entry[],
): Map<string, DaySums> {
	const sums = new Map<string, DaySums>();
	const addEntry = (e: Entry, kind: "exported" | "tracked") => {
		const parts = e.date.split("/");
		if (parts.length !== 3) return;
		const key = `${parts[2]}-${parts[0]}-${parts[1]}`;
		const prev = sums.get(key) ?? emptyDaySums();
		const isBillable = !e.matter_id || e.billable === true;
		if (kind === "exported") {
			if (isBillable) prev.exportedBillable += e.duration;
			else prev.exportedNonBillable += e.duration;
		} else {
			if (isBillable) prev.trackedBillable += e.duration;
			else prev.trackedNonBillable += e.duration;
		}
		sums.set(key, prev);
	};
	for (const e of entries) addEntry(e, "tracked");
	for (const e of exportedEntries) addEntry(e, "exported");
	return sums;
}
